import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UserSelectors } from "@dpdgroupuk/ai-app";

import { QuantumIcon } from "../../assets/icons/QuantumIcon";
import { AppsIcon } from "../../assets/icons/AppsIcon";
import { LocationIcon } from "../../assets/icons/Location";
import { getBoundariesAndActiveOverlayDates } from "../../utils/api";
import {
  setPageLoading,
  setVolumeOverlayDate,
  setDefaultOverlayData,
  setSimulatedVolumeOverlayData,
  setShowErrorModal,
  setErrorModalMessage,
  setSimulationPercentage,
  setActiveDatesForSimulationDatePicker,
} from "../../reducers/app";
import { setUserDetails } from "../../reducers/user";
import {
  setDepot,
  setBoundariesForSingleDepot,
  setManifesetLocations,
} from "../../reducers/depot";
import { setMapBounds, setShowManifestLocationStops } from "../../reducers/map";
import { pageToShowIndexSelector } from "../../reducers/app/selector";
import {
  firebaseUserSelector,
  depotUserListSelector,
} from "../../reducers/user/selector";
import {
  depotCodeSelector,
  depotNameSelector,
} from "../../reducers/depot/selector";
import { getUserData } from "../../utils/api";
import { getDepotBounds } from "../../utils/map/getDepotBounds";
import { UserMenu } from "./UserMenu";
import { SubHeader } from "../SubHeader";
import { groupByPostcodeLevel } from "../../utils/groupByPostcodeLevel";
import { emailToSpannerId } from "../../utils/string";
import { VolumeOverlay } from "./VolumeOverlay";
import "./Header.css";

const { REACT_APP_ORACLE_ENV } = process.env;

export const Header = ({ children }) => {
  const selectedLocation = useSelector(depotNameSelector);
  const [activeDropDown, setActiveDropDown] = useState("none");
  const [locationSearch, setLocationSearch] = useState(selectedLocation);

  const pageToShowIndex = useSelector(pageToShowIndexSelector);
  const firebaseUser = useSelector(firebaseUserSelector);
  const oracleUser = useSelector(UserSelectors.getUser);
  const depotList = useSelector(depotUserListSelector);
  const currentDepot = useSelector(depotCodeSelector);

  const user = REACT_APP_ORACLE_ENV ? oracleUser : firebaseUser;

  const spannerUserId =
    emailToSpannerId(useSelector(UserSelectors.getUser)?.email) || "";

  const dispatch = useDispatch();

  useEffect(() => {
    // dispatch(resetAppState());
    const findUser = async () => {
      try {
        dispatch(setPageLoading(true));
        const user = await getUserData(spannerUserId);

        dispatch(
          setUserDetails({
            depotUserList: user.data.user.depotAccess,
            spannerUserId,
            firebaseUser: { displayName: "", email: "" },
          })
        );
        dispatch(setPageLoading(false));
      } catch (e) {
        console.log(e);
        dispatch(setShowErrorModal(true));
        dispatch(setErrorModalMessage("Getting User Depot Data"));
        dispatch(setPageLoading(false));
      }
    };

    findUser();
  }, [dispatch, spannerUserId]);

  const LocationDropDown = () => {
    const optionsGenerator = () => {
      const filteredDepots = depotList.filter((depot) => {
        const depotName = depot.depotName?.toLowerCase() || "";
        const depotCode = depot.depotCode?.toLowerCase() || "";
        const locationSearchLowerCase = locationSearch.toLowerCase();

        return (
          depotName !== null &&
          depotCode !== null &&
          (locationSearchLowerCase === "" ||
            depotName.includes(locationSearchLowerCase) ||
            depotCode.includes(locationSearchLowerCase) ||
            locationSearchLowerCase.includes(depotName) ||
            locationSearchLowerCase.includes(depotCode))
        );
      });

      return filteredDepots
        .map((depot) => {
          const depotName = depot.depotName || "";
          const depotCode = depot.depotCode || "";
          return (
            <li
              key={depotCode}
              className="headerLocationDropDownValue"
              id={depotCode}
              name={`${depotName} (${depotCode})`}
              onClick={async () => {
                try {
                  dispatch(setPageLoading(true));
                  const name = `${depotName} (${depotCode})`;
                  setLocationSearch(name);
                  dispatch(setDepot({ depotName: name, depotCode }));

                  // No need to re-call APIs if the Depot doesn't change
                  if (currentDepot !== depotCode && depotName !== null) {
                    dispatch(setVolumeOverlayDate(null));
                    const getDepotBoundariesAndDates =
                      await getBoundariesAndActiveOverlayDates(depotCode);

                    const { boundaries, overlayDates, manifestLocations } =
                      getDepotBoundariesAndDates.data[depotCode];

                    // Split the boundaries up into different arrays based on the type of feature
                    const groupedBoundaries = groupByPostcodeLevel(
                      boundaries.map((groupedBoundaries) => ({
                        ...groupedBoundaries,
                        depotCode,
                      }))
                    );

                    dispatch(setManifesetLocations(manifestLocations));

                    dispatch(
                      setMapBounds(
                        getDepotBounds(groupedBoundaries.districtBoundaries)
                      )
                    );
                    dispatch(setBoundariesForSingleDepot(groupedBoundaries));

                    dispatch(
                      setActiveDatesForSimulationDatePicker(overlayDates)
                    );
                    dispatch(setDefaultOverlayData([]));
                    dispatch(setSimulationPercentage(0));
                    dispatch(setSimulatedVolumeOverlayData(null));
                    dispatch(setShowManifestLocationStops(false));
                  }
                  dispatch(setPageLoading(false));
                } catch (e) {
                  dispatch(setShowErrorModal(true));
                  dispatch(setErrorModalMessage("Getting Depot Information"));
                  setLocationSearch("");
                  dispatch(setDepot({ depotName: "", depotCode: "" }));
                  dispatch(setPageLoading(false));
                }
              }}
            >
              {`${depotName} (${depotCode})`}
            </li>
          );
        })
        .reverse();
    };

    return (
      <ul
        className="headerLocationDropDown"
        onClick={() => setActiveDropDown("none")}
      >
        {depotList?.length ? (
          optionsGenerator()
        ) : (
          <li className="headerLocationDropDownValue" id="" name="">
            No Options
          </li>
        )}
      </ul>
    );
  };

  return (
    <div onClick={() => setActiveDropDown("none")}>
      <div
        className="header"
        style={{
          backgroundColor: REACT_APP_ORACLE_ENV
            ? "rgba(48, 48, 48, 1)"
            : "rgba(220, 0, 50, 1)",
        }}
      >
        {!REACT_APP_ORACLE_ENV && <QuantumIcon />}
        <SubHeader />
        <div className="headerOptions">
          {selectedLocation !== "" && (
            <VolumeOverlay
              activeDropDown={activeDropDown}
              setActiveDropDown={setActiveDropDown}
            />
          )}
          {/* {pageToShowIndex === 0 && selectedLocation !== "" && (
            <VolumeOverlay
              overlayDate={volumeOverlayDate}
              setDefaultData={setDefaultOverlayData}
              setOverlayDate={setVolumeOverlayDate}
            />
          )}
          {pageToShowIndex === 1 && (
            <VolumeOverlay
              overlayDate={fullMapOverlayDate}
              setDefaultData={setFullMapParcelList}
              setOverlayDate={setFullMapOverlayDate}
            />
          )} */}
          {pageToShowIndex === 0 && (
            <div
              className="headerSegment"
              style={{ position: "relative" }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <input
                className="headerDepotSearchBox"
                id="headerDepotSearchBox"
                value={locationSearch}
                autoComplete="off"
                onClick={(e) => {
                  e.target.select();
                  setActiveDropDown("location");
                }}
                onChange={(e) => {
                  if (e.target.value === "") {
                    dispatch(setDepot({ depotName: "", depotCode: "" }));
                  }
                  setLocationSearch(e.target.value);
                }}
                placeholder="Please Select"
              />
              <LocationIcon
                className="headerIcon"
                onClick={() => {
                  document.getElementById("headerDepotSearchBox").select();
                  setActiveDropDown("location");
                }}
              />
              {activeDropDown === "location" && <LocationDropDown />}
            </div>
          )}
          <div className="headerMenu">
            <div className="headerUserName">
              {user?.displayName || `${user?.firstName} ${user?.lastName}`}
            </div>
            <div
              className={REACT_APP_ORACLE_ENV && "oracleHeaderActive"}
              onClick={(e) => {
                e.stopPropagation();
                if (activeDropDown === "usermenu") {
                  setActiveDropDown("none");
                } else {
                  setActiveDropDown("usermenu");
                }
              }}
            >
              <AppsIcon className="headerIcon" />
            </div>
          </div>
        </div>
        {activeDropDown === "usermenu" && <UserMenu user={user} />}
      </div>
      {/* <SubHeader /> */}
      <div className="page">{children}</div>
    </div>
  );
};
